import './applicantUnemploymentTypePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/shared_new/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: { isCurrentIncomePersisted },
    currentIncomeEmploymentType,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setCurrentIncomeEmploymentType,
    syncIncome,
  } = useApplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      employmentTypeValue: currentIncomeEmploymentType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<EmploymentType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const employmentTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(currentIncomeEmploymentType) && isValidClickEvent(event),
    );

    setCurrentIncomeEmploymentType(employmentTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employmentTypeValue } = values;

    if (!employmentTypeValue) {
      return;
    }

    if (isCurrentIncomePersisted && isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-unemployment-type-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.applicantUnemploymentType.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantUnemploymentType.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantUnemploymentType.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantUnemploymentType.progress.root}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantUnemploymentType.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantUnemploymentType.form.h1}>
                {t('ApplicantUnemploymentTypePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantUnemploymentType.form.h2}>
                {t('ApplicantUnemploymentTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantUnemploymentType.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={currentIncomeEmploymentType}
                  name="employmentTypeValue"
                  options={[
                    {
                      label: t('ApplicantUnemploymentTypePage.disability'),
                      value: EmploymentType.DISABILITY,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.homemaker'),
                      value: EmploymentType.HOMEMAKER,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption2,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.retired'),
                      value: EmploymentType.RETIRED,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption3,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.student'),
                      value: EmploymentType.STUDENT,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption4,
                    },
                    {
                      label: t('ApplicantUnemploymentTypePage.other'),
                      value: EmploymentType.OTHER,
                      testId:
                        locator.pages.applicantUnemploymentType.form
                          .radioOption5,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantUnemploymentType.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applicantUnemploymentType.form.submitButton
                  }
                  disabled={isEmpty(currentIncomeEmploymentType)}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantUnemploymentTypePage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantUnemploymentType.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantUnemploymentTypePage';
